import { ArrowRight, CircleAlert, ShieldCheck, Volume2 } from "lucide-react";
import { Tape } from "@/components/Brand";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const AREA_RULES = [
  "Jangan berdiri atau lewat di bawah orang yang sedang manjat.",
  "Jangan pegang atau mainkan tali dan alat yang sedang dipakai.",
  "Belum bisa belay? Jangan coba sendiri, minta didampingi tim.",
  "Ini ruang publik: barang pribadi tanggung jawab masing-masing.",
];

const CALLS = [
  {
    climber: "On belay?",
    belayer: "Belay on!",
  },
  {
    climber: "Climbing!",
    belayer: "Climb on!",
  },
];

const SAFETY_CALLS = [
  {
    call: "Slack!",
    meaning: "Tolong kendurkan talinya.",
  },
  {
    call: "Take!",
    meaning: "Tolong tegangkan talinya. Mau istirahat.",
  },
  {
    call: "Lower!",
    meaning: "Aku mau turun.",
  },
  {
    call: "Falling!",
    meaning: "Aku mau jatuh. Teriak sebelum lepas.",
  },
];

export function ClimbingGuide() {
  return (
    <section
      className="overflow-hidden rounded-[22px] bg-white ring-1 ring-[#1A3323]/10"
      data-testid="climbing-guide"
    >
      <div className="px-5 pb-3 pt-5">
        <Tape>panduan di wall</Tape>
        <h2
          className="mt-2 text-xl font-extrabold text-[#1A3323]"
          data-testid="climbing-guide-heading"
        >
          Siap manjat dengan aman
        </h2>
        <p className="mt-1 text-sm text-[#1A3323]/65">
          Buka panduan sebelum mulai naik.
        </p>
      </div>

      <Accordion type="single" collapsible className="px-5" data-testid="climbing-guide-accordion">
        <AccordionItem value="area-rules" className="border-[#1A3323]/10">
          <AccordionTrigger
            className="py-4 no-underline hover:no-underline"
            data-testid="area-rules-trigger"
          >
            <span className="flex items-center gap-3 text-left">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E3EDD9]">
                <ShieldCheck className="h-5 w-5 text-[#1A3323]" />
              </span>
              <span>
                <span
                  className="block text-base font-extrabold text-[#1A3323]"
                  data-testid="area-rules-heading"
                >
                  Aturan Area
                </span>
                <span className="mt-0.5 block text-xs font-normal text-[#1A3323]/60">
                  Etika dan keselamatan saat di wall
                </span>
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-5">
            <ul className="space-y-3" data-testid="area-rules-list">
              {AREA_RULES.map((rule, index) => (
                <li
                  key={rule}
                  className="flex gap-2.5 text-sm leading-relaxed text-[#1A3323]/85"
                  data-testid={`area-rule-${index + 1}`}
                >
                  <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-sm bg-[#3D6F4B]" />
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
            <div
              className="mt-5 border-l-4 border-[#7C9A6E] bg-[#F5F3EB] px-4 py-3 text-[#1A3323]"
              data-testid="ask-first-callout"
            >
              <div className="flex items-center gap-2 font-extrabold">
                <CircleAlert className="h-4 w-4" />
                Ragu? Stop. Tanya dulu.
              </div>
              <p className="mt-1 text-sm text-[#1A3323]/80">
                Di wall ini nggak ada lomba siapa yang paling berani.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="climbing-calls" className="border-b-0 border-[#1A3323]/10">
          <AccordionTrigger
            className="py-4 no-underline hover:no-underline"
            data-testid="climbing-calls-trigger"
          >
            <span className="flex items-center gap-3 text-left">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E3EDD9]">
                <Volume2 className="h-5 w-5 text-[#1A3323]" />
              </span>
              <span>
                <span
                  className="block text-base font-extrabold text-[#1A3323]"
                  data-testid="climbing-calls-heading"
                >
                  Climbing Calls
                </span>
                <span className="mt-0.5 block text-xs font-normal text-[#1A3323]/60">
                  Panggilan jelas antara pemanjat dan belayer
                </span>
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-5">
            <p className="text-sm leading-relaxed text-[#1A3323]/75">
              Belayer nggak bisa baca pikiran. Ucapkan dengan suara keras, menghadap belayer,
              dan tunggu dijawab sebelum naik.
            </p>

            <div className="mt-4 space-y-2" data-testid="standard-calls-list">
              {CALLS.map((item, index) => (
                <div
                  key={item.climber}
                  className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 bg-[#F5F3EB] px-3 py-3 text-[#1A3323]"
                  data-testid={`standard-call-${index + 1}`}
                >
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-[#1A3323]/60">
                      Pemanjat
                    </div>
                    <div className="mt-0.5 font-extrabold">{item.climber}</div>
                  </div>
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  <div className="text-right">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-[#1A3323]/60">
                      Belayer
                    </div>
                    <div className="mt-0.5 font-extrabold">{item.belayer}</div>
                  </div>
                </div>
              ))}
            </div>

            <h3
              className="mt-5 text-sm font-extrabold uppercase tracking-wide text-[#1A3323]"
              data-testid="safety-calls-heading"
            >
              Selama Manjat
            </h3>
            <div className="mt-3 grid grid-cols-2 gap-2" data-testid="safety-calls-list">
              {SAFETY_CALLS.map((item, index) => (
                <div
                  key={item.call}
                  className="min-h-[112px] border border-[#1A3323]/20 bg-[#FDFCF8] p-3"
                  data-testid={`safety-call-${index + 1}`}
                >
                  <div className="text-xl font-extrabold text-[#3D6F4B]">{item.call}</div>
                  <p className="mt-1.5 text-xs leading-relaxed text-[#1A3323]/80">
                    {item.meaning}
                  </p>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
}